import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CRUNCH_FLAVORS, BYO_PIECE_PRICE, BOX_SIZES, FREE_SHIPPING_THRESHOLD, STANDARD_SHIPPING } from '../mock';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { Plus, Minus, X, Check, ShoppingBag, ArrowRight, Trash2, Copy, Lock } from 'lucide-react';

const emptyBox = (size, form = 'crunch') => ({
  id: `box-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  size,
  form,
  flavors: [],
});

const FlavorVisual = ({ f, className = '' }) =>
  f.image ? (
    <img src={f.image} alt={f.name} className={className} />
  ) : (
    <div
      className={`${className} rounded-full flex flex-col items-center justify-center text-[#f6efe1]`}
      style={{ background: `linear-gradient(160deg, ${f.color} 0%, #1a0d08 100%)` }}
    >
      <span className="tracking-luxe text-[8px] opacity-80">CRUNCH</span>
      <span className="font-display text-base mt-0.5">{f.name}</span>
    </div>
  );

const BuildBox = () => {
  const { addBuildBox } = useCart();
  const navigate = useNavigate();
  const [boxes, setBoxes] = useState([emptyBox(8)]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [formType, setFormType] = useState('crunch');
  const [addingAll, setAddingAll] = useState(false);

  const activeBox = boxes[activeIdx];
  const totalInActive = activeBox.flavors.reduce((s, f) => s + f.count, 0);
  const remaining = activeBox.size - totalInActive;

  const setSize = (size) => {
    const next = [...boxes];
    let box = { ...next[activeIdx], size, flavors: [...next[activeIdx].flavors] };
    let total = box.flavors.reduce((s, f) => s + f.count, 0);
    while (total > size) {
      const last = box.flavors[box.flavors.length - 1];
      if (!last) break;
      if (last.count > 1) { last.count -= 1; total -= 1; }
      else { box.flavors.pop(); total -= 1; }
    }
    next[activeIdx] = box;
    setBoxes(next);
  };

  const addFlavor = (flavor) => {
    if (remaining <= 0) return;
    const next = [...boxes];
    const box = { ...next[activeIdx], flavors: [...next[activeIdx].flavors] };
    const existing = box.flavors.find((f) => f.id === flavor.id);
    if (existing) existing.count += 1;
    else box.flavors.push({ id: flavor.id, name: flavor.name, image: flavor.image, count: 1 });
    next[activeIdx] = box;
    setBoxes(next);
  };

  const decFlavor = (flavorId) => {
    const next = [...boxes];
    const box = { ...next[activeIdx], flavors: [...next[activeIdx].flavors] };
    const item = box.flavors.find((f) => f.id === flavorId);
    if (!item) return;
    item.count -= 1;
    if (item.count <= 0) box.flavors = box.flavors.filter((f) => f.id !== flavorId);
    next[activeIdx] = box;
    setBoxes(next);
  };

  const addAnotherBox = () => {
    const nb = emptyBox(activeBox.size);
    setBoxes([...boxes, nb]);
    setActiveIdx(boxes.length);
  };

  const duplicateBox = () => {
    const clone = {
      ...activeBox,
      id: `box-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      flavors: activeBox.flavors.map((f) => ({ ...f })),
    };
    const nb = [...boxes, clone];
    setBoxes(nb);
    setActiveIdx(nb.length - 1);
  };

  const removeBox = (idx) => {
    if (boxes.length === 1) return;
    const nb = boxes.filter((_, i) => i !== idx);
    setBoxes(nb);
    setActiveIdx(Math.min(activeIdx, nb.length - 1));
  };

  const clearActiveBox = () => {
    const nb = [...boxes];
    nb[activeIdx] = { ...nb[activeIdx], flavors: [] };
    setBoxes(nb);
  };

  const summary = React.useMemo(() => {
    const boxTotals = boxes.map((b) => {
      const count = b.flavors.reduce((s, f) => s + f.count, 0);
      return { id: b.id, size: b.size, count, price: b.size * BYO_PIECE_PRICE };
    });
    const readyBoxes = boxTotals.filter((b) => b.count === b.size).length;
    const subtotal = boxTotals
      .filter((b) => b.count === b.size)
      .reduce((s, b) => s + b.price, 0);
    const shipping = subtotal >= FREE_SHIPPING_THRESHOLD || subtotal === 0 ? 0 : STANDARD_SHIPPING;
    const grandTotal = subtotal + shipping;
    return { boxTotals, readyBoxes, subtotal, shipping, grandTotal };
  }, [boxes]);

  const addAllToCart = async () => {
    const readyBoxes = boxes.filter(
      (b) => b.flavors.reduce((s, f) => s + f.count, 0) === b.size
    );
    if (readyBoxes.length === 0) return;

    setAddingAll(true);
    let added = 0;
    try {
      for (const b of readyBoxes) {
        // Sequential, not parallel — each add-to-cart call rotates the
        // Cart-Token, so firing them in parallel risks a later request
        // using a token a concurrent request already invalidated.
        // eslint-disable-next-line no-await-in-loop
        await addBuildBox({
          size: b.size,
          flavors: b.flavors.map((f) => ({
            id: f.id,
            name: f.name,
            count: f.count,
          })),
        });
        added += 1;
      }
      toast.success(`Added ${added} box${added > 1 ? 'es' : ''} to your cart`);
    } catch (err) {
      toast.error(
        added > 0
          ? `Added ${added} box${added > 1 ? 'es' : ''}, then hit an error`
          : 'Could not add your box to the cart',
        { description: err.message }
      );
    } finally {
      setAddingAll(false);
    }
  };

  return (
    <main>
      <section className="bg-[#1a120b] text-[#f6efe1] py-20">
        <div className="container-lux text-center">
          <p className="tracking-luxe text-[#b78d3c] mb-4">The Ritual</p>
          <h1 className="font-display text-6xl md:text-7xl mb-4">Curate a Box.</h1>
          <p className="text-[#f6efe1]/70 max-w-xl mx-auto">
            Choose a form. Choose a size. Compose your selection, piece by piece.
          </p>
        </div>
      </section>

      <section className="container-lux pt-10">
        <p className="tracking-luxe text-[#b78d3c] mb-4">Choose the form</p>
        <div className="grid grid-cols-2 gap-3 max-w-2xl">
          <button
            onClick={() => setFormType('crunch')}
            className={`p-6 text-left border transition-colors ${
              formType === 'crunch'
                ? 'bg-[#1a120b] text-[#f6efe1] border-[#1a120b]'
                : 'border-[#1a120b]/25 hover:border-[#1a120b]'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <p className="tracking-luxe text-[10px]">The Crunch</p>
              {formType === 'crunch' && (
                <Check className="w-4 h-4 text-[#b78d3c]" />
              )}
            </div>
            <p className="font-display text-2xl">Signature pieces</p>
            <p className="text-xs opacity-70 mt-1">13 flavours available now</p>
          </button>
          <button
            disabled
            onClick={(e) => {
              e.preventDefault();
              navigate('/barks');
            }}
            className="p-6 text-left border border-[#1a120b]/15 relative cursor-not-allowed group"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="tracking-luxe text-[10px] text-[#1a120b]/60">The Barks</p>
              <Lock className="w-3.5 h-3.5 text-[#1a120b]/40" />
            </div>
            <p className="font-display text-2xl text-[#1a120b]/45">Hand-broken slabs</p>
            <p className="text-xs text-[#1a120b]/50 mt-1">
              Monsoon 2026 ·{' '}
              <Link
                to="/barks"
                className="underline underline-offset-2 text-[#b78d3c] hover:text-[#8f6c25]"
              >
                Notify me
              </Link>
            </p>
            <div className="absolute inset-0 bg-[#f6efe1]/40 pointer-events-none"></div>
            <span className="absolute top-3 right-3 bg-[#1a120b] text-[#f6efe1] px-2 py-0.5 text-[9px] tracking-widest">
              COMING SOON
            </span>
          </button>
        </div>
      </section>

      <section className="container-lux py-10 grid lg:grid-cols-[1fr_400px] gap-10">
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-6">
            {boxes.map((b, idx) => {
              const filled = b.flavors.reduce((s, f) => s + f.count, 0);
              const done = filled === b.size;
              return (
                <button
                  key={b.id}
                  onClick={() => setActiveIdx(idx)}
                  className={`group relative px-4 py-3 border transition-colors ${
                    idx === activeIdx
                      ? 'bg-[#1a120b] text-[#f6efe1] border-[#1a120b]'
                      : 'border-[#1a120b]/25 text-[#1a120b] hover:border-[#1a120b]'
                  }`}
                >
                  <span className="tracking-widest uppercase text-[10px] block">
                    Box {idx + 1}
                  </span>
                  <span className="text-sm font-medium">
                    {b.size}pc · {filled}/{b.size}
                    {done && ' ✓'}
                  </span>
                  {boxes.length > 1 && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        removeBox(idx);
                      }}
                      className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#b78d3c] text-[#1a120b] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </span>
                  )}
                </button>
              );
            })}
            <button
              onClick={addAnotherBox}
              className="px-4 py-3 border border-dashed border-[#1a120b]/40 text-[#1a120b]/70 hover:border-[#1a120b] hover:text-[#1a120b] flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm tracking-widest uppercase">Add box</span>
            </button>
            <button
              onClick={duplicateBox}
              className="ml-auto px-3 py-3 text-xs tracking-widest uppercase text-[#1a120b]/70 hover:text-[#1a120b] inline-flex items-center gap-1.5"
            >
              <Copy className="w-3.5 h-3.5" /> Duplicate
            </button>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full bg-[#1a120b] text-[#f6efe1] flex items-center justify-center text-sm font-medium">
                1
              </span>
              <h2 className="font-display text-2xl">Choose a size</h2>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {BOX_SIZES.map((s) => (
                <button
                  key={s.size}
                  onClick={() => setSize(s.size)}
                  className={`p-5 border transition-all ${
                    activeBox.size === s.size
                      ? 'bg-[#1a120b] text-[#f6efe1] border-[#1a120b]'
                      : 'border-[#1a120b]/20 hover:border-[#1a120b]'
                  }`}
                >
                  <p className="font-display text-4xl">{s.size}</p>
                  <p className="tracking-widest uppercase text-[10px] mt-1 opacity-80">
                    {s.label}
                  </p>
                  <p className="text-xs mt-2 opacity-70">₹{s.size * BYO_PIECE_PRICE}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full bg-[#1a120b] text-[#f6efe1] flex items-center justify-center text-sm font-medium">
                2
              </span>
              <h2 className="font-display text-2xl">Compose the selection</h2>
              <span className="ml-auto text-sm text-[#1a120b]/60">
                {totalInActive}/{activeBox.size} filled
              </span>
            </div>

            <div className="flex gap-1.5 mb-6">
              {[...Array(activeBox.size)].map((_, i) => (
                <div
                  key={i}
                  className={`h-2 flex-1 transition-colors ${
                    i < totalInActive ? 'bg-[#b78d3c]' : 'bg-[#1a120b]/10'
                  }`}
                ></div>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {CRUNCH_FLAVORS.map((f) => {
                const inBox = activeBox.flavors.find((x) => x.id === f.id);
                const disabled = remaining <= 0 && !inBox;
                return (
                  <div
                    key={f.id}
                    className={`relative bg-[#efe6d3] border-2 transition-all ${
                      inBox ? 'border-[#1a120b]' : 'border-transparent'
                    } ${disabled ? 'opacity-40' : ''}`}
                  >
                    <div className="aspect-square rounded-full halo overflow-hidden relative">
                      <FlavorVisual
                        f={f}
                        className="w-full h-full object-cover"
                      />
                      {inBox && (
                        <span className="absolute top-2 right-2 bg-[#b78d3c] text-[#1a120b] font-display text-sm w-7 h-7 rounded-full flex items-center justify-center">
                          {inBox.count}
                        </span>
                      )}
                    </div>
                    <div className="p-3 border-t border-[#1a120b]/10">
                      <p className="font-display text-lg leading-tight">{f.name}</p>
                      <p className="text-[10px] tracking-widest uppercase text-[#1a120b]/50 mt-0.5">
                        ₹{BYO_PIECE_PRICE}/pc
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        {inBox ? (
                          <div className="flex items-center border border-[#1a120b] w-full">
                            <button
                              onClick={() => decFlavor(f.id)}
                              className="w-8 h-8 flex items-center justify-center hover:bg-[#1a120b]/5"
                            >
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="flex-1 text-center text-sm">
                              {inBox.count}
                            </span>
                            <button
                              onClick={() => addFlavor(f)}
                              disabled={disabled}
                              className="w-8 h-8 flex items-center justify-center hover:bg-[#1a120b]/5 disabled:opacity-40"
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => addFlavor(f)}
                            disabled={disabled}
                            className="w-full text-xs tracking-widest uppercase border border-[#1a120b] py-2 hover:bg-[#1a120b] hover:text-[#f6efe1] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Add
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {activeBox.flavors.length > 0 && (
              <button
                onClick={clearActiveBox}
                className="mt-6 inline-flex items-center gap-2 text-sm text-[#1a120b]/60 hover:text-[#1a120b]"
              >
                <Trash2 className="w-4 h-4" /> Clear this box
              </button>
            )}
          </div>
        </div>

        <aside className="lg:sticky lg:top-28 h-fit">
          <div className="bg-[#1a120b] text-[#f6efe1] p-7">
            <p className="tracking-luxe text-[#b78d3c] mb-2">Your curation</p>
            <h3 className="font-display text-3xl mb-6">Summary</h3>

            <div className="space-y-4 mb-6">
              {boxes.map((b, i) => {
                const filled = b.flavors.reduce((s, f) => s + f.count, 0);
                const done = filled === b.size;
                return (
                  <div key={b.id} className="border-b border-[#f6efe1]/12 pb-4">
                    <div className="flex justify-between items-baseline">
                      <p className="font-medium">
                        Box {i + 1} - {b.size}pc
                      </p>
                      <p
                        className={
                          done
                            ? 'text-[#b78d3c] tracking-widest text-xs uppercase'
                            : 'text-[#f6efe1]/50 text-xs uppercase tracking-widest'
                        }
                      >
                        {done ? 'Ready' : `${filled}/${b.size}`}
                      </p>
                    </div>
                    {b.flavors.length === 0 ? (
                      <p className="text-xs text-[#f6efe1]/45 mt-1">Empty, choose pieces</p>
                    ) : (
                      <p className="text-xs text-[#f6efe1]/70 mt-1">
                        {b.flavors.map((f) => `${f.count}× ${f.name}`).join(' · ')}
                      </p>
                    )}
                    <p className="text-sm mt-2">₹{b.size * BYO_PIECE_PRICE}</p>
                  </div>
                );
              })}
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#f6efe1]/70">Ready</span>
                <span>
                  {summary.readyBoxes} of {boxes.length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#f6efe1]/70">Subtotal</span>
                <span>₹{summary.subtotal}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#f6efe1]/70">Shipping</span>
                <span>
                  {summary.shipping === 0 ? 'Complimentary' : `₹${summary.shipping}`}
                </span>
              </div>
              <div className="gold-rule my-3"></div>
              <div className="flex justify-between font-display text-2xl">
                <span>Total</span>
                <span>₹{summary.grandTotal}</span>
              </div>
            </div>

            <button
              onClick={addAllToCart}
              disabled={summary.readyBoxes === 0 || addingAll}
              className="btn-gold w-full mt-6 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ShoppingBag className="w-4 h-4" />
              {addingAll
                ? 'Adding…'
                : `Add ${summary.readyBoxes > 0 ? `${summary.readyBoxes} box${summary.readyBoxes > 1 ? 'es' : ''}` : 'to Cart'}`}
            </button>
            {summary.readyBoxes === 0 && (
              <p className="text-xs text-[#f6efe1]/50 text-center mt-3">
                Complete one box to continue
              </p>
            )}
          </div>

          <div className="mt-6 space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#b78d3c]" /> Free shipping on ₹400+
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#b78d3c]" /> Handwritten cards at checkout
            </div>
          </div>
        </aside>
      </section>
    </main>
  );
};

export default BuildBox;
